import {
  IcAutoFocus,
  IcBalance,
  IcCapture,
  IcCrosshair,
  IcFactoryPosition,
  IcFocusIn,
  IcFocusOut,
  IcFullScreen,
  IcGauge,
  IcIso,
  IcJoystick,
  IcManualFocus,
  IcParkingPosition,
  IcPip,
  IcPolarity,
  IcRound,
  IcSearch,
  IcSensorTracking,
  IcSync,
  IcVideoTracking,
  IcVirtualJoystick,
  IcVmsFilter,
  IcZoomIn,
  IcZoomOut,
} from '@assets/icons/32x32';
import { Box, ButtonGroup, HStack, Icon, Popover, SliderValueChangeDetails, Text, VStack } from '@chakra-ui/react';
import ButtonBarPanel from '@components/common/inputs/buttons/ButtonBarPanel';
import PanelHeader from '@components/common/layout/PanelHeader';
import MenuList from '@components/common/menu/MenuList';
import RoundContent from '@components/map/popup/round/RoundContent';
import { ButtonBar } from '@components/ui/buttonBar';
import { Slider } from '@components/ui/slider';
import { createToast } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { UseCameraCommandFunctions } from '@hooks/useCameraCommand';
import { useSelectorWithMapIdFromContext } from '@hooks/useSelectorWithMapIdFromContext';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole, hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { updateJoystickControlId } from '@redux/global/global.reducer';
import { selectJoystickControlId, selectJoystickControlledCam } from '@redux/global/global.selector';
import { selectCameraConfigurationByUniqueKey } from '@redux/sensors/sensors.selectors';
import { selectSelectedTargets } from '@redux/situation/situation.selectors';
import { modulo } from '@utils/math.utils';
import {
  CameraAvailableFeatures,
  cameraAvailableFeatures,
  MAX_FOCUS_PRESET,
  subCamera,
  SubCameraType,
} from '@utils/sensors/camera/camera.constants';
import { isEqual } from 'lodash';
import { Dispatch, useMemo } from 'react';
import { FaSnowflake } from 'react-icons/fa';
import { useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { CameraConfiguration, CameraType } from '@/types/sensor/configuration.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import {
  FocusModeEnum,
  IsoModeEnum,
  TrackingStatusEnum,
  TrackingTypeEnum,
  WhiteBalanceModeEnum,
} from '@/types/sensor/sensors';

import { ControlState, GaugeStyleEnum } from './CameraControl';
import CameraTakeControlButton from './CameraTakeControlButton';
import MWIRFiltersMenu from './menu/MWIRFiltersMenu';

type Props = {
  monitoringSensor: MonitoringSensorByType<CameraType> | null;
  selectedCameraKey: string | null;
  replayMode: boolean;
  controlState: ControlState;
  setControlState: Dispatch<Partial<ControlState>>;
  useCamera: UseCameraCommandFunctions;
  uniqueKey: string;
  downloadScreenshot: () => void;
};

export default function CameraLadButtons({
  monitoringSensor,
  selectedCameraKey,
  replayMode,
  controlState,
  uniqueKey,
  useCamera,
  downloadScreenshot,
  setControlState,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const updateJoystickControlIdWithDispatch = useWithDispatch(updateJoystickControlId);
  const joystickControlId = useAppSelector(selectJoystickControlId);
  const sensorAction = { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.CAMERA };
  const accessCamCommand = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, sensorAction));
  const controlledCamera = useAppSelector(selectJoystickControlledCam);
  const canDeice = useAppSelector((state) => hasRole(state, RoleEnum.DEICING_CAMERA_SENSOR));
  const cameraStatus = monitoringSensor?.status ?? null;
  const videoTrackingStateOfSelectedFlux =
    (
      controlState.displayedFlux &&
      cameraStatus?.trackingStatus.videoTrackingStatus.holopticsVideoTrackings[controlState.displayedFlux.cameraId]
    )?.status ?? TrackingStatusEnum.DISABLED;
  const cameraStoreConfig: CameraConfiguration | null = useAppSelector(
    (state) => selectCameraConfigurationByUniqueKey(state, selectedCameraKey),
    isEqual,
  );

  const isBufferized =
    controlState.displayedFlux !== null && cameraStatus?.trackingStatus.videoTrackingStatus.holopticsVideoTrackings
      ? cameraStatus.trackingStatus.videoTrackingStatus.holopticsVideoTrackings[controlState.displayedFlux.cameraId]
          ?.isBufferized
      : false;

  const cameraConfiguration = monitoringSensor?.configuration ?? null;
  const cameraType = cameraStatus?.type ?? cameraStoreConfig?.type ?? null;

  const subCameraStatus =
    cameraStatus?.videoStatuses.find((videoStatus) => videoStatus.id === controlState.displayedFlux?.cameraId) ?? null;
  const subCameraType =
    cameraConfiguration &&
    subCameraStatus &&
    subCamera[cameraConfiguration.type].find((value) => value.fluxId === subCameraStatus.id);
  const isTracking =
    cameraStatus?.trackingStatus.globalTrackingState === TrackingStatusEnum.ACTIVE ||
    cameraStatus?.trackingStatus.globalTrackingState === TrackingStatusEnum.LOST;
  const sensorTrackingState =
    cameraStatus?.trackingStatus.sensorTrackingStatus.globalSensorTrackingState ?? TrackingStatusEnum.DISABLED;
  const isSensorTracking =
    isTracking &&
    cameraStatus.trackingStatus.globalTrackingType === TrackingTypeEnum.SENSOR &&
    sensorTrackingState === TrackingStatusEnum.ACTIVE;

  const selectedTargets = useSelectorWithMapIdFromContext(selectSelectedTargets);
  const availableFeatures = cameraType ? cameraAvailableFeatures[cameraType] : null;
  const isDisabled = !useCamera.canCommand || !useCamera.isSubCameraAvailable;

  function handleSensorTracking() {
    if (useCamera.canCommand && !replayMode && accessCamCommand) {
      if (!isSensorTracking && sensorTrackingState !== TrackingStatusEnum.PENDING) {
        if (selectedTargets.length !== 0) {
          useCamera.sensorTracking(selectedTargets[selectedTargets.length - 1], false);
        } else {
          createToast(formatMessage({ id: 'target.noSelected' }));
        }
      } else {
        useCamera.sensorTracking(null, false);
      }
    }
  }

  const isAutoFocusOn = subCameraStatus?.focusMode === FocusModeEnum.AUTO;

  function handleWhiteBalanceClick(value: WhiteBalanceModeEnum) {
    useCamera.setWhiteBalance(value);
    setControlState({ whiteBalance: !controlState.whiteBalance });
  }

  function handleOpenMenu(newState: Partial<ControlState>, reset = false) {
    if (reset) {
      setControlState({ iso: false, whiteBalance: false, round: false, hybridFocus: false, ...newState });
    } else {
      setControlState(newState);
    }
  }

  function handleIsoClick(value: IsoModeEnum) {
    useCamera.setIso(value);
    handleOpenMenu({ iso: !controlState.iso });
  }

  function isFeatureActive(feature: keyof CameraAvailableFeatures, subCameraType?: SubCameraType): boolean {
    if (!availableFeatures) {
      return false;
    }

    const featureValue = availableFeatures[feature];

    if (featureValue === undefined) {
      return false;
    }

    if (typeof featureValue === 'boolean') {
      return featureValue;
    } else if (subCameraType !== undefined) {
      return featureValue.includes(subCameraType);
    }
    return false;
  }

  const focusLevel = useMemo(
    () => (subCameraStatus?.focusLevel || subCameraStatus?.focusLevel === 0 ? [subCameraStatus.focusLevel] : [10]),
    [subCameraStatus?.focusLevel],
  );

  if (replayMode || !accessCamCommand || !selectedCameraKey) {
    return (
      <HStack gap={5}>
        {selectedCameraKey && <CameraTakeControlButton cameraStatus={cameraStatus} replayMode={replayMode} />}
        <ButtonGroup gap={0}>
          <ButtonBar.Root
            aria-label={formatMessage({ id: 'cameras.cameraMenu.capture' })}
            size="md"
            disabled={cameraStatus === null}
            onClick={downloadScreenshot}
          >
            <Icon as={IcCapture} />
          </ButtonBar.Root>
          <ButtonBar.Root
            aria-label={formatMessage({ id: 'cameras.cameraMenu.crosshair' })}
            size="md"
            data-active={controlState.displayCross}
            isHidden={!selectedCameraKey}
            onClick={() => {
              setControlState({ displayCross: !controlState.displayCross });
            }}
          >
            <Icon as={IcCrosshair} />
          </ButtonBar.Root>
          <ButtonBar.Root
            aria-label={formatMessage({ id: 'cameras.cameraMenu.gaugeStyle' })}
            size="md"
            isHidden={!selectedCameraKey}
            onClick={() => {
              const styles = Object.values(GaugeStyleEnum);
              setControlState({
                gaugeStyle: styles[modulo(styles.indexOf(controlState.gaugeStyle) + 1, 4)],
              });
            }}
          >
            <Icon as={IcGauge} />
          </ButtonBar.Root>
          <ButtonBar.Root
            aria-label={formatMessage({ id: 'cameras.cameraMenu.fullscreen' })}
            size="md"
            data-active={controlState.fullscreen}
            onClick={() => setControlState({ fullscreen: !controlState.fullscreen })}
          >
            <Icon as={IcFullScreen} />
          </ButtonBar.Root>
        </ButtonGroup>
      </HStack>
    );
  }

  return (
    <HStack gap={4}>
      <ButtonBarPanel
        icon={IcPip}
        isActive={controlState.pip}
        isDisabled={controlState.pipFlux.length === 0}
        onClick={() => setControlState({ pip: !controlState.pip })}
      />
      <CameraTakeControlButton useCamera={useCamera} cameraStatus={cameraStatus} />
      <ButtonGroup gap={0}>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.sensorTracking' })}
          size="md"
          colorPalette="blue"
          data-selected={sensorTrackingState === TrackingStatusEnum.ACTIVE}
          data-active={sensorTrackingState === TrackingStatusEnum.PENDING}
          disabled={isDisabled}
          onClick={handleSensorTracking}
        >
          <Icon as={IcSensorTracking} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.videoTracking' })}
          size="md"
          colorPalette="red"
          data-selected={videoTrackingStateOfSelectedFlux === TrackingStatusEnum.ACTIVE}
          shouldFlicker={isBufferized && videoTrackingStateOfSelectedFlux !== TrackingStatusEnum.ACTIVE}
          disabled={isDisabled}
          onClick={() => useCamera.videoBuffering()}
        >
          <Icon as={IcVideoTracking} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.autoTracking' })}
          size="md"
          colorPalette="red"
          shouldFlicker={videoTrackingStateOfSelectedFlux === TrackingStatusEnum.SCANNING}
          disabled={isDisabled}
          onClick={() => useCamera.videoScanning()}
          //TODO tracker-auto change icon
        >
          <Icon as={IcSearch} />
        </ButtonBar.Root>
        <Popover.Root
          open={controlState.round}
          positioning={{ placement: 'top' }}
          onOpenChange={(e) => {
            handleOpenMenu({ round: e.open }, e.open);
          }}
        >
          <Popover.Trigger asChild>
            <Box as="span">
              <ButtonBar.Root
                aria-label={formatMessage({ id: 'rounds.roundPopup' })}
                size="md"
                hideTooltip={controlState.round}
                onClick={() => handleOpenMenu({ round: !controlState.round })}
                isHidden={!availableFeatures?.roundOn}
                disabled={!availableFeatures?.roundOn || !useCamera.canCommand}
                data-active={controlState.round}
              >
                <ButtonBar.Control>
                  <Icon as={IcRound} />
                </ButtonBar.Control>
              </ButtonBar.Root>
            </Box>
          </Popover.Trigger>
          <Popover.Positioner>
            <Popover.Content>
              <Popover.Body padding={0}>
                {selectedCameraKey && controlState.round && (
                  <VStack gap={0} height="fit-content" width="496px">
                    <PanelHeader
                      label={formatMessage({ id: 'rounds.roundPopup' })}
                      onClose={() => handleOpenMenu({ round: false })}
                    />
                    <RoundContent uniqueKey={selectedCameraKey} />
                  </VStack>
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Popover.Root>
      </ButtonGroup>
      <ButtonGroup gap={0}>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.joystick' })}
          size="md"
          colorPalette="orange"
          data-selected={joystickControlId === uniqueKey}
          data-active={controlledCamera.cameraUniqueKey === selectedCameraKey}
          onClick={() => updateJoystickControlIdWithDispatch(joystickControlId !== uniqueKey ? uniqueKey : null)}
        >
          <Icon as={IcJoystick} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.virtualJoystick' })}
          size="md"
          data-active={controlState.displayVirtualJoystick}
          onClick={() => {
            setControlState({ displayVirtualJoystick: !controlState.displayVirtualJoystick });
          }}
        >
          <Icon as={IcVirtualJoystick} />
        </ButtonBar.Root>
      </ButtonGroup>
      <ButtonGroup gap={0}>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.zoomIn' })}
          size="md"
          onMouseDown={() => useCamera.zoomIn()}
          onMouseUp={() => useCamera.zoomStop()}
          isHidden={!isFeatureActive('zoom', subCameraType?.type)}
          disabled={!isFeatureActive('zoom', subCameraType?.type) || isDisabled}
        >
          <Icon as={IcZoomIn} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.zoomOut' })}
          size="md"
          onMouseDown={() => useCamera.zoomOut()}
          onMouseUp={() => useCamera.zoomStop()}
          isHidden={!isFeatureActive('zoom', subCameraType?.type)}
          disabled={!isFeatureActive('zoom', subCameraType?.type) || isDisabled}
        >
          <Icon as={IcZoomOut} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.zoomSync' })}
          size="md"
          onClick={useCamera.zoomSync}
          isHidden={!isFeatureActive('zoomSync', subCameraType?.type)}
          disabled={!isFeatureActive('zoomSync', subCameraType?.type) || isDisabled}
        >
          <Icon as={IcSync} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.focusIn' })}
          size="md"
          onMouseDown={useCamera.focusIn}
          onMouseUp={useCamera.focusStop}
          isHidden={!isFeatureActive('focus', subCameraType?.type)}
          disabled={!isFeatureActive('focus', subCameraType?.type) || isDisabled}
        >
          <ButtonBar.Control>
            <Icon as={IcFocusIn} />
          </ButtonBar.Control>
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.focusOut' })}
          size="md"
          onMouseDown={useCamera.focusOut}
          onMouseUp={useCamera.focusStop}
          isHidden={!isFeatureActive('focus', subCameraType?.type)}
          disabled={!isFeatureActive('focus', subCameraType?.type) || isDisabled}
        >
          <Icon as={IcFocusOut} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.autoFocus' })}
          size="md"
          onClick={isAutoFocusOn ? useCamera.focusStop : useCamera.focusAuto}
          data-active={isAutoFocusOn}
          isHidden={!isFeatureActive('autoFocus', subCameraType?.type)}
          disabled={!isFeatureActive('autoFocus', subCameraType?.type) || isDisabled}
        >
          <Icon as={IcAutoFocus} />
        </ButtonBar.Root>
        <Popover.Root
          open={controlState.hybridFocus}
          positioning={{ placement: 'top' }}
          onOpenChange={(e) => {
            handleOpenMenu({ hybridFocus: e.open }, e.open);
          }}
        >
          <Popover.Trigger asChild>
            <Box as="span">
              <ButtonBar.Root
                aria-label={formatMessage({ id: 'cameras.cameraMenu.hybridFocus' })}
                size="md"
                data-active={controlState.hybridFocus}
                isHidden={!isFeatureActive('hybridFocus', subCameraType?.type)}
                disabled={!isFeatureActive('hybridFocus', subCameraType?.type) || isDisabled}
                onClick={() => handleOpenMenu({ hybridFocus: !controlState.hybridFocus })}
              >
                <ButtonBar.Control>
                  <Icon as={IcManualFocus} />
                </ButtonBar.Control>
              </ButtonBar.Root>
            </Box>
          </Popover.Trigger>
          <Popover.Positioner>
            <Popover.Content>
              <Popover.Body padding={2}>
                {controlState.hybridFocus && (
                  <HStack gap={2}>
                    <Slider
                      width="100%"
                      min={0}
                      showArrows
                      max={MAX_FOCUS_PRESET}
                      value={focusLevel}
                      onValueChange={(value: SliderValueChangeDetails) =>
                        useCamera.hybridFocus((value.value.at(0)! * 100) / MAX_FOCUS_PRESET)
                      }
                    />
                    <Text color="neutral.300">{focusLevel}</Text>
                  </HStack>
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Popover.Root>
      </ButtonGroup>
      <ButtonGroup gap={0}>
        <Popover.Root
          open={controlState.iso}
          positioning={{ placement: 'top' }}
          onOpenChange={(e) => {
            handleOpenMenu({ iso: e.open }, e.open);
          }}
        >
          <Popover.Trigger asChild>
            <Box as="span">
              <ButtonBar.Root
                aria-label={formatMessage({ id: 'cameras.cameraMenu.iso' })}
                size="md"
                data-active={controlState.iso}
                onClick={() => handleOpenMenu({ iso: !controlState.iso })}
                isHidden={!isFeatureActive('iso', subCameraType?.type)}
                disabled={!isFeatureActive('iso', subCameraType?.type) || !subCameraStatus || isDisabled}
              >
                <ButtonBar.Control>
                  <Icon as={IcIso} />
                </ButtonBar.Control>
              </ButtonBar.Root>
            </Box>
          </Popover.Trigger>
          <Popover.Positioner>
            <Popover.Content>
              <Popover.Body padding={0}>
                {controlState.iso && (
                  <MenuList
                    parentLabel="cameras.cameraMenu.iso"
                    menuItems={Object.entries(IsoModeEnum).reduce(
                      (accumulator, currentValue) => ({
                        ...accumulator,
                        [currentValue[1]]: {
                          label: `cameras.cameraMenu.${currentValue[0]}`,
                          onAction: () => handleIsoClick(currentValue[1]),
                        },
                      }),
                      {},
                    )}
                    selectedOptions={[`${subCameraStatus?.iso}`]}
                  />
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Popover.Root>
        <Popover.Root
          open={controlState.whiteBalance}
          positioning={{ placement: 'top' }}
          onOpenChange={(e) => {
            handleOpenMenu({ whiteBalance: e.open }, e.open);
          }}
        >
          <Popover.Trigger asChild>
            <Box as="span">
              <ButtonBar.Root
                aria-label={formatMessage({ id: 'cameras.cameraMenu.whiteBalance' })}
                size="md"
                data-active={controlState.whiteBalance}
                onClick={() => handleOpenMenu({ whiteBalance: !controlState.whiteBalance })}
                isHidden={!isFeatureActive('whiteBalance', subCameraType?.type)}
                disabled={!isFeatureActive('whiteBalance', subCameraType?.type) || !subCameraStatus || isDisabled}
              >
                <ButtonBar.Control>
                  <Icon as={IcBalance} />
                </ButtonBar.Control>
              </ButtonBar.Root>
            </Box>
          </Popover.Trigger>
          <Popover.Positioner>
            <Popover.Content>
              <Popover.Body padding={0}>
                {controlState.whiteBalance && (
                  <MenuList
                    parentLabel="cameras.cameraMenu.whiteBalance"
                    menuItems={Object.entries(WhiteBalanceModeEnum).reduce(
                      (accumulator, currentValue) => ({
                        ...accumulator,
                        [currentValue[1]]: {
                          label: `cameras.cameraMenu.${currentValue[0]}`,
                          onAction: () => handleWhiteBalanceClick(currentValue[1]),
                        },
                      }),
                      {},
                    )}
                    selectedOptions={[`${subCameraStatus?.whiteBalance}`]}
                  />
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Popover.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.polarity' })}
          size="md"
          data-active={subCameraStatus?.blackHot}
          onClick={() => {
            useCamera.setPolarity(!subCameraStatus?.blackHot);
          }}
          isHidden={!isFeatureActive('polarity', subCameraType?.type)}
          disabled={!isFeatureActive('polarity', subCameraType?.type) || !subCameraStatus || isDisabled}
        >
          <Icon as={IcPolarity} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.activateIr' })}
          size="md"
          data-active={subCameraStatus?.irOn}
          onClick={() => {
            useCamera.activateIr(!subCameraStatus?.irOn);
          }}
          isHidden={!isFeatureActive('activateIr', subCameraType?.type)}
          disabled={!isFeatureActive('activateIr', subCameraType?.type) || !subCameraStatus || isDisabled}
        >
          <Icon as={IcPolarity} />
        </ButtonBar.Root>
        <Popover.Root
          open={controlState.mwirFilters}
          positioning={{ placement: 'top' }}
          onOpenChange={(e) => {
            handleOpenMenu({ mwirFilters: e.open }, e.open);
          }}
        >
          <Popover.Trigger asChild>
            <Box as="span">
              <ButtonBar.Root
                aria-label={formatMessage({ id: 'cameras.cameraMenu.mwirFilters' })}
                size="md"
                data-active={controlState.mwirFilters}
                onClick={() => handleOpenMenu({ mwirFilters: !controlState.mwirFilters })}
                isHidden={!isFeatureActive('mwirFilters', subCameraType?.type)}
                disabled={!isFeatureActive('mwirFilters', subCameraType?.type) || !subCameraStatus || isDisabled}
              >
                <ButtonBar.Control>
                  <Icon as={IcVmsFilter} />
                </ButtonBar.Control>
              </ButtonBar.Root>
            </Box>
          </Popover.Trigger>
          <Popover.Positioner>
            <Popover.Content>
              <Popover.Body padding={0}>
                {controlState.mwirFilters && monitoringSensor?.status && subCameraStatus && (
                  <MWIRFiltersMenu monitoringSensor={monitoringSensor} subCameraStatus={subCameraStatus} />
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Popover.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.deIcing' })}
          size="md"
          data-active={subCameraStatus?.deIcing}
          onClick={() => {
            useCamera.deIcing(!subCameraStatus?.deIcing);
          }}
          isHidden={!isFeatureActive('deIcing', subCameraType?.type)}
          disabled={!isFeatureActive('deIcing', subCameraType?.type) || !subCameraStatus || isDisabled || !canDeice}
        >
          <ButtonBar.Control>
            <Icon width="32px" height="32px" as={FaSnowflake} />
          </ButtonBar.Control>
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.nuc' })}
          size="md"
          onClick={() => useCamera.nuc()}
          isHidden={!isFeatureActive('nuc', subCameraType?.type)}
          disabled={!isFeatureActive('nuc', subCameraType?.type) || !subCameraStatus || isDisabled}
        >
          <ButtonBar.Control>
            <Text fontWeight="medium">{formatMessage({ id: 'cameras.cameraMenu.nuc' })}</Text>
          </ButtonBar.Control>
        </ButtonBar.Root>
      </ButtonGroup>
      <ButtonGroup gap={0}>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.capture' })}
          size="md"
          onClick={downloadScreenshot}
          disabled={cameraStatus === null}
        >
          <Icon as={IcCapture} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.crosshair' })}
          size="md"
          data-active={controlState.displayCross}
          onClick={() => {
            setControlState({ displayCross: !controlState.displayCross });
          }}
        >
          <Icon as={IcCrosshair} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.gaugeStyle' })}
          size="md"
          isHidden={!selectedCameraKey}
          onClick={() => {
            const styles = Object.values(GaugeStyleEnum);
            setControlState({ gaugeStyle: styles[modulo(styles.indexOf(controlState.gaugeStyle) + 1, 4)] });
          }}
        >
          <Icon as={IcGauge} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.fullscreen' })}
          size="md"
          data-active={controlState.fullscreen}
          onClick={() => setControlState({ fullscreen: !controlState.fullscreen })}
        >
          <Icon as={IcFullScreen} />
        </ButtonBar.Root>
      </ButtonGroup>
      <ButtonGroup gap={0}>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.factory' })}
          size="md"
          onClick={useCamera.factoryPosition}
          isHidden={!isFeatureActive('factoryPosition', subCameraType?.type)}
          disabled={!isFeatureActive('factoryPosition', subCameraType?.type) || !subCameraStatus || isDisabled}
        >
          <Icon as={IcFactoryPosition} />
        </ButtonBar.Root>
        <ButtonBar.Root
          aria-label={formatMessage({ id: 'cameras.cameraMenu.parking' })}
          size="md"
          onClick={useCamera.parkingPosition}
          isHidden={!isFeatureActive('parkingPosition', subCameraType?.type)}
          disabled={!isFeatureActive('parkingPosition', subCameraType?.type) || !subCameraStatus || isDisabled}
        >
          <Icon as={IcParkingPosition} />
        </ButtonBar.Root>
      </ButtonGroup>
    </HStack>
  );
}
